import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// `pulse_company_requests` and `profiles` aren't in the generated Database
// types in the explorer, so cast through `any` for these queries.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb: any = supabase;

export type CompanyRequestStatus = 'pending' | 'completed' | 'failed' | string;

export interface CompanyRequest {
  id: string;
  task_id: string;
  slug: string;
  pricing_page_url: string;
  status: CompanyRequestStatus;
  requested_by: string;
  team_id: string | null;
  requested_at: string;
  completed_at: string | null;
  canonical_slug: string | null;
  failure_reason: string | null;
  failure_message: string | null;
  last_synced_at: string;
}

export interface ProfileSummary {
  id: string;
  email: string | null;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

export function useCompanyRequests(opts: {
  status?: CompanyRequestStatus;
  limit?: number;
} = {}) {
  const { status, limit = 200 } = opts;
  return useQuery({
    queryKey: ['company-requests', { status, limit }],
    queryFn: async (): Promise<CompanyRequest[]> => {
      let q = sb
        .from('pulse_company_requests')
        .select(
          'id, task_id, slug, pricing_page_url, status, requested_by, team_id, requested_at, completed_at, canonical_slug, failure_reason, failure_message, last_synced_at'
        )
        .order('requested_at', { ascending: false })
        .limit(limit);
      if (status) q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as CompanyRequest[];
    },
    staleTime: 30_000,
  });
}

export interface ClaudeTaskSummary {
  id: string;
  status: string;
  command: string | null;
}

export function useClaudeTasksByIds(ids: string[]) {
  const sorted = [...new Set(ids.filter(Boolean))].sort();
  return useQuery({
    queryKey: ['claude-tasks-by-ids', sorted],
    queryFn: async (): Promise<Map<string, ClaudeTaskSummary>> => {
      if (sorted.length === 0) return new Map();
      const { data, error } = await sb
        .from('claude_tasks')
        .select('id, status, command')
        .in('id', sorted);
      if (error) throw error;
      const m = new Map<string, ClaudeTaskSummary>();
      for (const t of (data ?? []) as ClaudeTaskSummary[]) m.set(t.id, t);
      return m;
    },
    enabled: sorted.length > 0,
    // Tasks change state — keep this short so a pending->running->completed
    // transition shows up without a hard reload.
    staleTime: 15_000,
  });
}

export function useProfiles(ids: string[]) {
  const sorted = [...new Set(ids)].sort();
  return useQuery({
    queryKey: ['profiles', sorted],
    queryFn: async (): Promise<Map<string, ProfileSummary>> => {
      if (sorted.length === 0) return new Map();
      const { data, error } = await sb
        .from('profiles')
        .select('id, email, display_name, first_name, last_name, avatar_url')
        .in('id', sorted);
      if (error) throw error;
      const m = new Map<string, ProfileSummary>();
      for (const p of (data ?? []) as ProfileSummary[]) m.set(p.id, p);
      return m;
    },
    enabled: sorted.length > 0,
    staleTime: 5 * 60_000,
  });
}
